import React from 'react';
import Header from '../../../shared/components/layout/Header';

export const BOQ_BOM_APP_TITLE = 'BOQ & BOM Generator';
export const BOQ_BOM_GENERATED_SUBTITLE = 'Review, edit and save your BOQ';

const sidebarOffsetClass = (collapsed) => (collapsed ? 'left-[76px] right-0' : 'left-64 right-0');

const BreadcrumbChevron = () => (
  <span className="mx-2 text-[#9CA3AF] shrink-0" aria-hidden="true">
    &gt;
  </span>
);

export const BoqBomDetailBreadcrumb = ({ projectName, flowLabel, currentLabel }) => (
  <nav
    className="flex items-center min-w-0 text-[16px] leading-snug text-[#6B7280] font-medium"
    aria-label="Breadcrumb">
    <span className="truncate min-w-0">{projectName}</span>
    <BreadcrumbChevron />
    <span className="truncate min-w-0">{flowLabel}</span>
    <BreadcrumbChevron />
    <span className="truncate font-semibold text-[#111827]">{currentLabel}</span>
  </nav>
);

export const BOQ_BOM_TOOLBAR_HEIGHT_CLASS = {
  default: 'h-[4.75rem]',
  withBackArrow: 'h-[5.25rem]'
};

/** In-flow spacer for list screen (toolbar only). Prefer pt on page content with matching bg instead. */
export const BoqBomToolbarSpacer = ({ withBackArrow = false, className = 'bg-[#F4F6F7]' }) => (
  <div
    aria-hidden="true"
    className={`shrink-0 w-full ${className} ${
      withBackArrow ? BOQ_BOM_TOOLBAR_HEIGHT_CLASS.withBackArrow : BOQ_BOM_TOOLBAR_HEIGHT_CLASS.default
    }`}
  />
);

export const BoqBomFixedToolbar = ({ collapsed, backArrow = false, onBack, children }) => (
  <div
    className={`fixed z-[10] top-0 border-b border-gray-200 shadow-sm transition-all duration-300 ease-in-out bg-white ${sidebarOffsetClass(
      collapsed
    )}`}>
    <Header backArrow={backArrow} onBack={onBack}>
      {children ?? (
        <h2 className="text-base md:text-lg font-semibold text-[#1E2328] truncate">
          {BOQ_BOM_APP_TITLE}
        </h2>
      )}
    </Header>
  </div>
);

export const BoqBomDetailTabs = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex items-stretch gap-8 bg-[#F4F6F7] px-6 md:px-8 border-b border-[#E5E7EB]">
    {tabs.map((tab) => {
      const isActive = activeTab === tab;
      return (
        <button
          key={tab}
          type="button"
          onClick={() => onTabChange(tab)}
          className={`relative py-3.5 text-[15px] leading-none transition-colors ${
            isActive ? 'text-[#2A6A73] font-semibold' : 'text-[#6B7280] font-medium hover:text-[#4B5563]'
          }`}>
          {tab}
          {isActive && (
            <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-[#2A9CB0] rounded-t-[2px]" />
          )}
        </button>
      );
    })}
  </div>
);

export const BoqBomPageHeader = ({
  title,
  subtitle = null,
  trailing = null,
  tabs = null,
  activeTab,
  onTabChange,
  embedded = false,
  className = ''
}) => {
  const hasTabs = tabs?.length > 0 && onTabChange;

  return (
    <div
      className={`bg-white ${embedded ? 'border-t border-[#ECEEF0]' : 'border-b border-[#ECEEF0]'} ${className}`}>
      <div
        className={`px-6 md:px-8 ${hasTabs ? 'pt-3 pb-3' : 'py-5'} ${hasTabs ? 'border-b border-[#ECEEF0]' : ''}`}>
        <div className={trailing ? 'flex items-center justify-between gap-6' : ''}>
          <div className="min-w-0">
            <h1 className="text-[20px] font-bold text-[#111827] leading-tight tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-[14px] font-normal text-[#6B7280] leading-snug">{subtitle}</p>
            )}
          </div>
          {trailing && <div className="shrink-0 flex items-center gap-5">{trailing}</div>}
        </div>
      </div>
      {hasTabs && <BoqBomDetailTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />}
    </div>
  );
};

/**
 * Single fixed chrome: app toolbar + page title (no gap between the two).
 */
export const BOQ_BOM_DETAIL_CONTENT_OFFSET = {
  withTabs: 'pt-[12rem]',
  withoutTabs: 'pt-[10rem]'
};

export const BoqBomDetailChrome = ({
  collapsed,
  backArrow = true,
  onBack,
  projectName,
  flowLabel,
  pageHeaderProps
}) => (
  <div
    className={`fixed z-[10] top-0 bg-white border-b border-[#ECEEF0] shadow-sm transition-all duration-300 ease-in-out ${sidebarOffsetClass(
      collapsed
    )}`}>
    <Header backArrow={backArrow} onBack={onBack}>
      <BoqBomDetailBreadcrumb
        projectName={projectName}
        flowLabel={flowLabel}
        currentLabel={pageHeaderProps?.breadcrumbLabel ?? pageHeaderProps?.title}
      />
    </Header>
    <BoqBomPageHeader
      embedded
      {...pageHeaderProps}
      subtitle={pageHeaderProps?.subtitle ?? BOQ_BOM_APP_TITLE}
    />
  </div>
);

/** Spacer matching combined fixed chrome height. Prefer pt on page content with matching bg instead. */
export const BoqBomDetailChromeSpacer = ({ hasTabs = false, className = 'bg-[#F4F6F7]' }) => (
  <div
    aria-hidden="true"
    className={`shrink-0 w-full ${className} ${hasTabs ? 'h-[12rem]' : 'h-[10rem]'}`}
  />
);
